import React, { useState } from 'react';
import { Mail, Linkedin, Send, Lock } from 'lucide-react';
import GuestExpandedDetails from './GuestExpandedDetails.jsx';
import { sharedStyles } from './shared/sharedStyles.js';

const GuestReportCard = ({
  guest,
  index,
  copiedIndex,
  onCopyEmail,
  onGeneratePitch,
  hasPremiumAccess = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  return (
    <>
      {/* Main Row */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'grid',
          gridTemplateColumns: '20% 8% 1fr 12% 10%',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid rgba(234, 88, 12, 0.08)',
          alignItems: 'center',
          transition: 'background 0.2s ease',
          cursor: 'pointer',
          height: '80px',
          position: 'relative',
          background: isExpanded ? 'rgba(234, 88, 12, 0.05)' : 'transparent',
          zIndex: isExpanded ? 100 : 1
        }}
        onMouseMove={e => {
          if (!isExpanded) {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPosition({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top
            });
          }
        }}
        onMouseEnter={e => {
          if (!isExpanded) {
            e.currentTarget.style.background = 'rgba(234, 88, 12, 0.03)';
            setShowTooltip(true);
          }
        }}
        onMouseLeave={e => {
          if (!isExpanded) e.currentTarget.style.background = 'transparent';
          setShowTooltip(false);
        }}
      >
        {/* Name Column */}
        <div style={{
          borderRight: '1px solid rgba(156, 163, 175, 0.3)',
          paddingRight: '0.5rem',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.25rem'
        }}>
          <div style={{
            fontWeight: '600',
            color: sharedStyles.colors.textPrimary,
            fontSize: '0.875rem',
            lineHeight: '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {guest.name}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: sharedStyles.colors.textSecondary,
            lineHeight: '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {guest.title}
          </div>
          <div style={{
            fontSize: '0.7rem',
            color: sharedStyles.colors.textTertiary,
            lineHeight: '1.2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {guest.company}
          </div>
        </div>

        {/* Match Score Column */}
        <div style={{
          textAlign: 'center',
          borderRight: '1px solid rgba(156, 163, 175, 0.3)',
          paddingRight: '0.5rem',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: sharedStyles.colors.primary
          }}>
            {(guest.relevanceScore * 10).toFixed(1)}
          </div>
        </div>

        {/* Summary Column */}
        <div style={{
          paddingRight: '0.75rem',
          fontSize: '0.8rem',
          color: sharedStyles.colors.textSecondary,
          lineHeight: '1.4',
          borderRight: '1px solid rgba(156, 163, 175, 0.3)',
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          <div style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {guest.summary}
          </div>
        </div>

        {/* Socials Column */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: hasPremiumAccess ? 'auto' : 'none',
          background: hasPremiumAccess ? 'transparent' : 'rgba(156, 163, 175, 0.15)',
          height: '100%',
          position: 'relative',
          zIndex: 1,
          borderRight: '1px solid rgba(156, 163, 175, 0.3)'
        }}>
          {!hasPremiumAccess ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              color: sharedStyles.colors.textTertiary,
              fontSize: '0.75rem'
            }}>
              <Lock size={14} />
              <span>Pro</span>
            </div>
          ) : (
            <>
              {guest.socialMedia?.email && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyEmail(guest.socialMedia.email, index);
                  }}
                  style={{
                    background: copiedIndex === index ? sharedStyles.colors.success : 'none',
                    border: 'none',
                    color: copiedIndex === index ? 'white' : sharedStyles.colors.primary,
                    padding: '0.25rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  title={guest.socialMedia.email}
                >
                  <Mail size={14} />
                </button>
              )}
              {guest.socialMedia?.linkedin && (
                <a
                  href={guest.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: sharedStyles.colors.primary,
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Linkedin size={14} />
                </a>
              )}
              {guest.socialMedia?.twitter && (
                <a
                  href={guest.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: sharedStyles.colors.primary,
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    fontFamily: 'Arial, sans-serif',
                    textDecoration: 'none'
                  }}
                >
                  𝕏
                </a>
              )}
            </>
          )}
        </div>

        {/* Pitch Column */}
        <div style={{
          textAlign: 'center',
          pointerEvents: hasPremiumAccess ? 'auto' : 'none',
          background: hasPremiumAccess ? 'transparent' : 'rgba(156, 163, 175, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          {!hasPremiumAccess ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              color: sharedStyles.colors.textTertiary,
              fontSize: '0.75rem'
            }}>
              <Lock size={14} />
              <span>Pro</span>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (guest.pitch) {
                  onGeneratePitch(index);
                }
              }}
              style={{
                background: guest.pitch ? sharedStyles.colors.primary : sharedStyles.colors.surfaceAlt,
                color: guest.pitch ? 'white' : sharedStyles.colors.textTertiary,
                border: 'none',
                borderRadius: '8px',
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                cursor: guest.pitch ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
              disabled={!guest.pitch}
            >
              <Send size={10} />
              {guest.pitch ? 'View' : 'N/A'}
            </button>
          )}
        </div>

        {/* Hover Tooltip */}
        {showTooltip && !isExpanded && (
          <div style={{
            position: 'absolute',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y + 16}px`,
            transform: 'translateX(-50%)',
            background: 'rgba(17, 24, 39, 0.85)',
            color: 'white',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            fontSize: '0.65rem',
            fontWeight: '500',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 150,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            Click to expand
          </div>
        )}

        {/* Expanded Details Section - Floating Overlay */}
        {isExpanded && (
          <GuestExpandedDetails guest={guest} hasPremiumAccess={hasPremiumAccess} />
        )}
      </div>
    </>
  );
};

export default GuestReportCard;